import { z } from 'zod';
import { AttachmentInput, ContentBase, GeoLocation } from './content.js';
import { Taxonomy } from './taxonomy.js';
import { CursorQuery } from './common/pagination.js';
/** Events & ticketing (FR-EVNT-01…08, AC-EVNT-01…04). */
export const EventAdmissionMode = z.enum(['open_free', 'rsvp_free', 'ticketed']);
export const CreateEventInput = Taxonomy.extend({
    communityId: z.string().min(1).optional(),
    title: z.string().min(1).max(120),
    body: z.string().max(5_000).optional(),
    venueName: z.string().min(1).max(160),
    address: z.string().min(1).max(240),
    location: GeoLocation.optional(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    /** IANA zone — displayed as authored, never device-local (NFR-15). */
    timezone: z.string().min(1).max(64),
    ticketAllocation: z.number().int().min(0).max(1_000_000),
    /** 0 = free event (Q-22 resolved as "price is set here"). */
    ticketPriceMinor: z.number().int().min(0).default(0),
    admissionMode: EventAdmissionMode.default('rsvp_free'),
    currency: z
        .string()
        .length(3)
        .regex(/^[A-Z]{3}$/)
        .default('USD'),
    commentsEnabled: z.boolean().default(true),
    opvEnabled: z.boolean().default(true),
    hashtags: z.array(z.string()).max(30).default([]),
    attachments: z.array(AttachmentInput).max(10).default([]),
    scheduledAt: z.coerce.date().optional(),
})
    .refine((e) => e.endsAt > e.startsAt, { message: 'endsAt must be after startsAt' })
    .refine((e) => e.admissionMode !== 'ticketed' || e.ticketPriceMinor > 0, {
    message: 'ticketed events require a price',
    path: ['ticketPriceMinor'],
})
    .refine((e) => e.admissionMode === 'open_free' || e.ticketAllocation > 0, {
    message: 'events requiring reservations must have available tickets',
    path: ['ticketAllocation'],
});
export const EventState = z.enum(['upcoming', 'live', 'expired']);
export const EventView = ContentBase.extend({
    type: z.literal('event'),
    venueName: z.string(),
    address: z.string(),
    startsAt: z.coerce.date(),
    endsAt: z.coerce.date(),
    timezone: z.string(),
    ticketAllocation: z.number().int(),
    ticketsBooked: z.number().int(),
    ticketsLeft: z.number().int(),
    ticketPriceMinor: z.number().int(),
    admissionMode: EventAdmissionMode,
    currency: z.string().length(3),
    state: EventState,
    /** viewer-relative */
    myTickets: z.number().int().optional(),
    interestedCount: z.number().int().nonnegative().default(0),
    /** viewer-relative; absent for anonymous responses */
    isInterested: z.boolean().optional(),
});
export const EventTab = z.enum(['all', 'upcoming', 'past']);
export const EventListQuery = CursorQuery.extend({
    communityId: z.string().min(1).optional(),
    tab: EventTab.default('all'),
    category1: Taxonomy.shape.category1.optional(),
    category2: Taxonomy.shape.category2.optional(),
});
export const BuyTicketInput = z.object({
    quantity: z.number().int().min(1).max(10),
    methodId: z.string().optional(),
    idempotencyKey: z.string().min(8).max(100),
});
export const TicketOrderView = z.object({
    id: z.string(),
    eventId: z.string(),
    quantity: z.number().int(),
    status: z.enum(['confirmed', 'failed', 'refunded']),
    paymentId: z.string().nullable(),
    failureReason: z.string().nullable(),
    createdAt: z.coerce.date(),
});
export const EventInterestView = z.object({
    eventId: z.string(),
    interested: z.boolean(),
    interestedCount: z.number().int().nonnegative(),
});
export const SetEventInterestInput = z.object({ interested: z.boolean() });
//# sourceMappingURL=event.js.map